import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { CategoryManagerButton } from '@/components/CategoryManagerButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ClipboardCheck,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Archive,
  Send,
  BarChart3,
  FileQuestion,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy,
  Loader2,
  Trophy,
  BookOpen,
  CalendarPlus,
  CalendarDays,
  Mail,
  Pencil,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';
import api, { categoryAPI, courseAPI } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  draft: { label: 'Brouillon', color: 'bg-gray-500', icon: AlertCircle },
  published: { label: 'Publié', color: 'bg-green-500', icon: CheckCircle },
  archived: { label: 'Archivé', color: 'bg-orange-500', icon: Archive },
};

export default function ExamListPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user, canManage } = useAuth();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [wsCategories, setWsCategories] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateExamId, setDuplicateExamId] = useState(null);
  const [allWorkspaces, setAllWorkspaces] = useState([]);
  const [targetWorkspace, setTargetWorkspace] = useState('');
  const [duplicating, setDuplicating] = useState(false);
  const [myAttempts, setMyAttempts] = useState({});  // { examId: attemptId }
  const [courses, setCourses] = useState([]);
  const [courseFilter, setCourseFilter] = useState('all');
  const [groupBy, setGroupBy] = useState('category'); // 'category' or 'course'
  
  // Scheduling state
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [scheduleForm, setScheduleForm] = useState({ exam_title: '', course_id: '', date: '', time: '09:00' });
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [sendingNotifId, setSendingNotifId] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleTab, setScheduleTab] = useState('list');
  const [savingSchedule, setSavingSchedule] = useState(false);

  useEffect(() => {
    if (workspaceId) {
      loadExams();
      loadCategories();
      categoryAPI.list(workspaceId).then(res => setWsCategories(res.data || [])).catch(() => {});
      courseAPI.list(workspaceId).then(res => setCourses(res.data || [])).catch(() => {});
      // Load candidate's own attempts
      if (!canManage && user?.id) {
        loadMyAttempts();
      }
    }
  }, [workspaceId, statusFilter, categoryFilter, courseFilter]);

  const loadMyAttempts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/exams/attempts/user/${user.id}?workspace_id=${workspaceId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Map: examId -> latest attempt id
        const map = {};
        data.forEach(a => {
          if (!map[a.exam_id] || new Date(a.submitted_at) > new Date(map[a.exam_id].submitted_at)) {
            map[a.exam_id] = a;
          }
        });
        setMyAttempts(map);
      }
    } catch (e) {
      console.error('Failed to load my attempts:', e);
    }
  };

  const loadSchedules = async () => {
    setSchedulesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/exams/schedules?workspace_id=${workspaceId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setSchedules(await res.json());
      }
    } catch (e) {
      console.error('Failed to load schedules:', e);
    } finally {
      setSchedulesLoading(false);
    }
  };

  const handleCreateSchedule = async () => {
    if (!scheduleForm.exam_title || !scheduleForm.date || !scheduleForm.time) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setSavingSchedule(true);
    try {
      const scheduledAt = `${scheduleForm.date}T${scheduleForm.time}:00`;
      const params = new URLSearchParams({
        exam_title: scheduleForm.exam_title,
        workspace_id: workspaceId,
        scheduled_at: scheduledAt,
        user_id: user.id,
        duration_minutes: scheduleForm.duration || '60'
      });
      if (scheduleForm.course_id && scheduleForm.course_id !== 'none') {
        params.set('course_id', scheduleForm.course_id);
      }
      const res = await fetch(`${API_URL}/api/exams/schedules?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        toast.success('Examen planifié avec succès');
        setScheduleForm({ exam_title: '', course_id: '', date: '', time: '09:00' });
        loadSchedules();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la planification');
      }
    } catch (e) {
      toast.error('Erreur réseau');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      const res = await fetch(`${API_URL}/api/exams/schedules/${scheduleId}?user_id=${user.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        toast.success('Planification supprimée');
        loadSchedules();
      }
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSendNotification = async (schedule) => {
    setSendingNotifId(schedule.id);
    try {
      const res = await fetch(`${API_URL}/api/exams/schedules/${schedule.id}/notify?workspace_id=${schedule.workspace_id}&user_id=${user.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Notification envoyée à ${data.notified_count || 0} candidat(s)`);
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } catch (e) {
      toast.error('Erreur réseau');
    } finally {
      setSendingNotifId(null);
    }
  };

  const handleUpdateSchedule = async () => {
    if (!editingSchedule) return;
    try {
      const params = new URLSearchParams({ user_id: user.id });
      if (editingSchedule.scheduled_at) params.set('scheduled_at', editingSchedule.scheduled_at);
      if (editingSchedule.exam_title) params.set('exam_title', editingSchedule.exam_title);
      if (editingSchedule.duration_minutes) params.set('duration_minutes', String(editingSchedule.duration_minutes));
      const res = await fetch(`${API_URL}/api/exams/schedules/${editingSchedule.id}?${params}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        toast.success('Planification modifiée');
        setEditingSchedule(null);
        loadSchedules();
      }
    } catch (e) {
      toast.error('Erreur lors de la modification');
    }
  };

  const loadExams = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/exams/?workspace_id=${workspaceId}`;
      
      // Candidates only see visible exams
      if (!canManage) {
        url += `&visible_only=true`;
      }
      
      if (statusFilter && statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (categoryFilter && categoryFilter !== 'all') {
        url += `&category=${categoryFilter}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      if (courseFilter && courseFilter !== 'all') {
        url += `&linked_course_id=${courseFilter}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      setExams(data);
    } catch (error) {
      console.error('Failed to load exams:', error);
      toast.error('Erreur lors du chargement des examens');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/exams/questions/categories/list?workspace_id=${workspaceId}`);
      const data = await response.json();
      setCategories(data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadExams();
  };

  const handlePublish = async (examId) => {
    try {
      const response = await fetch(`${API_URL}/api/exams/${examId}/publish?workspace_id=${workspaceId}`, {
        method: 'POST',
      });
      if (response.ok) {
        toast.success('Examen publié');
        loadExams();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la publication');
      }
    } catch (error) {
      toast.error('Erreur lors de la publication');
    }
  };

  const handleArchive = async (examId) => {
    try {
      await fetch(`${API_URL}/api/exams/${examId}/archive?workspace_id=${workspaceId}`, {
        method: 'POST',
      });
      toast.success('Examen archivé');
      loadExams();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleDelete = async (examId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet examen ?')) return;
    
    try {
      await fetch(`${API_URL}/api/exams/${examId}?workspace_id=${workspaceId}`, {
        method: 'DELETE',
      });
      toast.success('Examen supprimé');
      loadExams();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleVisibility = async (examId) => {
    try {
      const res = await fetch(`${API_URL}/api/exams/${examId}/visibility?workspace_id=${workspaceId}`, {
        method: 'PATCH',
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        loadExams();
      } else {
        toast.error('Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors du changement de visibilité');
    }
  };

  const openDuplicateModal = async (examId) => {
    setDuplicateExamId(examId);
    setTargetWorkspace(workspaceId);
    try {
      const res = await fetch(`${API_URL}/api/workspaces`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAllWorkspaces(data);
      }
    } catch (e) {
      console.error('Failed to load workspaces:', e);
    }
    setDuplicateModalOpen(true);
  };

  const handleDuplicate = async () => {
    if (!duplicateExamId) return;
    setDuplicating(true);
    try {
      const url = `${API_URL}/api/exams/${duplicateExamId}/duplicate?workspace_id=${workspaceId}&target_workspace_id=${targetWorkspace}&user_id=${user?.id}`;
      const res = await fetch(url, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Examen dupliqué : ${data.title}`);
        setDuplicateModalOpen(false);
        if (targetWorkspace === workspaceId) {
          loadExams();
        } else {
          toast.info('L\'examen a été dupliqué dans un autre workspace');
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors de la duplication');
      }
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <WorkspaceLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ClipboardCheck className="h-7 w-7 text-primary" />
              Examens & Certifications
            </h1>
            <p className="text-muted-foreground mt-1">
              Créez et gérez vos examens de certification
            </p>
          </div>
          
          {canManage && (
            <div className="flex gap-2">
              <CategoryManagerButton />
              <Button 
                variant="outline"
                onClick={() => { setScheduleDialogOpen(true); loadSchedules(); }}
                data-testid="btn-planifications"
              >
                <CalendarDays className="h-4 w-4 mr-2" />
                Planifications
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate(`/workspace/${workspaceId}/exams/questions`)}
                data-testid="btn-question-bank"
              >
                <FileQuestion className="h-4 w-4 mr-2" />
                Banque de questions
              </Button>
              <Button 
                onClick={() => navigate(`/workspace/${workspaceId}/exams/new`)}
                data-testid="btn-create-exam"
              >
                <Plus className="h-4 w-4 mr-2" />
                Créer un examen
              </Button>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher un examen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-exams"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]" data-testid="filter-status">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="draft">Brouillon</SelectItem>
                  <SelectItem value="published">Publié</SelectItem>
                  <SelectItem value="archived">Archivé</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px]" data-testid="filter-category">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les catégories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {courses.length > 0 && (
                <Select value={courseFilter} onValueChange={setCourseFilter}>
                  <SelectTrigger className="w-[200px]" data-testid="filter-course">
                    <SelectValue placeholder="Cours" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les cours</SelectItem>
                    <SelectItem value="none">Sans cours</SelectItem>
                    {courses.map((course) => (
                      <SelectItem key={course.id} value={course.id}>
                        <span className="flex items-center gap-2">
                          {course.color && (
                            <span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: course.color }} />
                          )}
                          {course.title || course.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <Button type="submit" variant="secondary">
                Rechercher
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Grouping Toggle + Tabs */}
        {(() => {
          const hasCourses = courses.length > 0;
          const hasWsCats = wsCategories.length > 0;
          if (!hasCourses && !hasWsCats) return null;
          
          // Build course groups
          const courseMap = {};
          courses.forEach(c => { courseMap[c.id] = c; });
          
          const examsByCourse = exams.reduce((acc, e) => {
            const cid = e.linked_course_id;
            const isKnown = cid && courseMap[cid];
            const key = isKnown ? cid : '__no_course__';
            if (!acc[key]) acc[key] = [];
            acc[key].push(e);
            return acc;
          }, {});
          
          const courseGroups = [];
          courses.forEach(c => {
            if (examsByCourse[c.id]) courseGroups.push({ key: c.id, label: c.title || c.name, color: c.color, items: examsByCourse[c.id] });
          });
          if (examsByCourse['__no_course__']) courseGroups.push({ key: '__no_course__', label: 'Sans cours', color: null, items: examsByCourse['__no_course__'] });
          
          // Build category groups
          const examsByGroup = exams.reduce((acc, e) => {
            const catId = e.category_id;
            const isKnown = catId && wsCategories.some(c => c.id === catId);
            const key = isKnown ? catId : '__uncategorized__';
            if (!acc[key]) acc[key] = [];
            acc[key].push(e);
            return acc;
          }, {});
          const catGroups = [];
          wsCategories.forEach(cat => {
            if (examsByGroup[cat.id]) catGroups.push({ key: cat.id, label: cat.name, color: cat.color, items: examsByGroup[cat.id] });
          });
          if (examsByGroup['__uncategorized__']) catGroups.push({ key: '__uncategorized__', label: 'Sans catégorie', color: null, items: examsByGroup['__uncategorized__'] });
          
          const groups = groupBy === 'course' ? courseGroups : catGroups;
          
          if (groups.length === 0 && !hasCourses && !hasWsCats) return null;
          
          if (groups.length > 0 && !groups.some(g => g.key === activeTab)) {
            setTimeout(() => setActiveTab(groups[0]?.key), 0);
          }
          
          return (
            <div className="space-y-3">
              {/* Grouping toggle */}
              {(hasCourses || hasWsCats) && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground">Regrouper par :</span>
                  {hasWsCats && (
                    <button
                      onClick={() => { setGroupBy('category'); setActiveTab(null); }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        groupBy === 'category'
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      Catégorie
                    </button>
                  )}
                  {hasCourses && (
                    <button
                      onClick={() => { setGroupBy('course'); setActiveTab(null); }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        groupBy === 'course'
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <BookOpen className="h-3.5 w-3.5" />
                      Cours
                    </button>
                  )}
                </div>
              )}
              
              {/* Group tabs */}
              {groups.length > 0 && (
                <div className="flex gap-2 flex-wrap pb-2 border-b border-border overflow-x-auto">
                  {groups.map(group => (
                    <button
                      key={group.key}
                      onClick={() => setActiveTab(group.key)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                        activeTab === group.key
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      {group.color && (
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: group.color }} />
                      )}
                      {groupBy === 'course' && !group.color && (
                        <BookOpen className="h-3.5 w-3.5 shrink-0" />
                      )}
                      {group.label}
                      <span className="ml-1 bg-background/50 text-xs px-1.5 py-0.5 rounded-full">{group.items.length}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Exams Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : exams.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">Aucun examen</h3>
                <p className="text-muted-foreground mb-4">
                  Commencez par créer votre premier examen
                </p>
                {canManage && (
                  <Button onClick={() => navigate(`/workspace/${workspaceId}/exams/new`)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Créer un examen
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Examen</TableHead>
                    <TableHead className="hidden md:table-cell">Questions</TableHead>
                    <TableHead className="hidden md:table-cell">Durée</TableHead>
                    <TableHead className="hidden sm:table-cell">Statut</TableHead>
                    <TableHead className="hidden lg:table-cell">Créé le</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    // Determine groups based on groupBy
                    const hasGroups = (groupBy === 'category' && wsCategories.length > 0) || (groupBy === 'course' && courses.length > 0);
                    
                    if (hasGroups && activeTab) {
                      if (groupBy === 'course') {
                        return exams.filter(e => {
                          if (activeTab === '__no_course__') {
                            return !e.linked_course_id || !courses.some(c => c.id === e.linked_course_id);
                          }
                          return e.linked_course_id === activeTab;
                        });
                      } else {
                        return exams.filter(e => {
                          if (activeTab === '__uncategorized__') {
                            return !e.category_id || !wsCategories.some(c => c.id === e.category_id);
                          }
                          return e.category_id === activeTab;
                        });
                      }
                    }
                    return exams;
                  })().map((exam) => {
                    const status = statusConfig[exam.status] || statusConfig.draft;
                    const StatusIcon = status.icon;
                    
                    return (
                      <TableRow key={exam.id} data-testid={`exam-row-${exam.id}`}>
                        <TableCell>
                          <div
                            className="cursor-pointer hover:text-primary transition-colors"
                            onClick={() => {
                              if (canManage) {
                                navigate(`/workspace/${workspaceId}/exams/${exam.id}`);
                              } else if (exam.status === 'published') {
                                navigate(`/workspace/${workspaceId}/exams/${exam.id}/take`);
                              }
                            }}
                          >
                            <div className="font-medium flex items-center gap-2">
                              {exam.title}
                              {canManage && exam.is_visible === false && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-orange-300 text-orange-600 bg-orange-50 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/30">
                                  <EyeOff className="h-3 w-3 mr-0.5" />
                                  Masqué
                                </Badge>
                              )}
                            </div>
                            {exam.category && (
                              <Badge variant="outline" className="mt-1">
                                {exam.category}
                              </Badge>
                            )}
                            {exam.linked_course_id && (() => {
                              const course = courses.find(c => c.id === exam.linked_course_id);
                              if (!course) return null;
                              return (
                                <Badge variant="secondary" className="mt-1 gap-1">
                                  <BookOpen className="h-3 w-3" />
                                  {course.title || course.name}
                                </Badge>
                              );
                            })()}
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="flex items-center gap-1">
                            <FileQuestion className="h-4 w-4 text-muted-foreground" />
                            {exam.question_count || 0}
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            {exam.settings?.duration_minutes || 60} min
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <Badge className={`${status.color} text-white`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-muted-foreground">
                          {formatDate(exam.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          {canManage ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`exam-actions-${exam.id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/workspace/${workspaceId}/exams/${exam.id}`)}>
                                <Eye className="h-4 w-4 mr-2" />
                                Voir les détails
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/workspace/${workspaceId}/exams/${exam.id}/edit`)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/workspace/${workspaceId}/exams/${exam.id}/stats`)}>
                                <BarChart3 className="h-4 w-4 mr-2" />
                                Statistiques
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/workspace/${workspaceId}/exams/${exam.id}/attempts`)}>
                                <Users className="h-4 w-4 mr-2" />
                                Tentatives
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {exam.status === 'draft' && (
                                <DropdownMenuItem onClick={() => handlePublish(exam.id)}>
                                  <Send className="h-4 w-4 mr-2" />
                                  Publier
                                </DropdownMenuItem>
                              )}
                              {exam.status === 'published' && (
                                <DropdownMenuItem onClick={() => handleArchive(exam.id)}>
                                  <Archive className="h-4 w-4 mr-2" />
                                  Archiver
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => openDuplicateModal(exam.id)}>
                                <Copy className="h-4 w-4 mr-2" />
                                Dupliquer
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleToggleVisibility(exam.id)}>
                                {exam.is_visible === false ? (
                                  <><Eye className="h-4 w-4 mr-2" /> Afficher aux candidats</>
                                ) : (
                                  <><EyeOff className="h-4 w-4 mr-2" /> Masquer aux candidats</>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-destructive"
                                onClick={() => handleDelete(exam.id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          ) : (
                            exam.status === 'published' && (
                              <div className="flex gap-2 justify-end">
                                {myAttempts[exam.id] && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => navigate(`/workspace/${workspaceId}/exams/results/${myAttempts[exam.id].id}`)}
                                  >
                                    <Trophy className="h-4 w-4 mr-1" />
                                    Résultats
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  onClick={() => navigate(`/workspace/${workspaceId}/exams/${exam.id}/take`)}
                                >
                                  Passer l'examen
                                </Button>
                              </div>
                            )
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Duplicate Modal */}
      {duplicateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDuplicateModalOpen(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1 flex items-center gap-2">
              <Copy className="h-5 w-5 text-primary" />
              Dupliquer l'examen
            </h3>
            <p className="text-sm text-muted-foreground mb-4">Choisissez le workspace de destination</p>
            
            <div className="space-y-3">
              <label className="text-sm font-medium">Workspace de destination</label>
              <select
                value={targetWorkspace}
                onChange={(e) => setTargetWorkspace(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {allWorkspaces.map(ws => (
                  <option key={ws.id} value={ws.id}>
                    {ws.name} {ws.id === workspaceId ? '(actuel)' : ''}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setDuplicateModalOpen(false)}
                className="px-4 py-2 text-sm font-medium rounded-lg border hover:bg-muted transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleDuplicate}
                disabled={duplicating}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {duplicating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
                {duplicating ? 'Duplication...' : 'Dupliquer'}
              </button>
            </div>
          </div>
        </div>
      )}

        {/* Scheduling Dialog */}
        <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
          <DialogContent className="sm:max-w-[750px] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <CalendarDays className="h-5 w-5 text-indigo-500" />
                Planification des examens
              </DialogTitle>
              <DialogDescription>
                Planifiez et gérez les dates de passage des examens
              </DialogDescription>
            </DialogHeader>

            {/* Tab buttons */}
            <div className="flex gap-1 rounded-lg bg-muted p-1">
              <button
                className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-all ${scheduleTab === 'list' ? 'bg-white dark:bg-zinc-800 shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => setScheduleTab('list')}
              >
                📋 Examens planifiés ({schedules.length})
              </button>
              <button
                className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-all ${scheduleTab === 'create' ? 'bg-white dark:bg-zinc-800 shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => setScheduleTab('create')}
              >
                ➕ Planifier un examen
              </button>
            </div>

            {/* CREATE TAB */}
            {scheduleTab === 'create' && (
              <div className="rounded-xl border bg-muted/30 p-4 space-y-4">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4 text-indigo-500" />
                  Nouveau planning
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Nom de l'examen *</Label>
                    <Input
                      className="h-10"
                      placeholder="Ex: Linux - Examen final"
                      value={scheduleForm.exam_title || ''}
                      onChange={(e) => setScheduleForm({...scheduleForm, exam_title: e.target.value})}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Cours (optionnel)</Label>
                    <Select
                      value={scheduleForm.course_id || 'none'}
                      onValueChange={(v) => setScheduleForm({...scheduleForm, course_id: v === 'none' ? '' : v})}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Aucun cours" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun cours</SelectItem>
                        {courses.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Date *</Label>
                    <Input
                      type="date"
                      className="h-10"
                      value={scheduleForm.date}
                      onChange={(e) => setScheduleForm({...scheduleForm, date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Heure *</Label>
                    <Input
                      type="time"
                      className="h-10"
                      value={scheduleForm.time}
                      onChange={(e) => setScheduleForm({...scheduleForm, time: e.target.value})}
                    />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs">Durée (minutes)</Label>
                    <Input
                      type="number"
                      className="h-10"
                      min="10"
                      step="5"
                      placeholder="60"
                      value={scheduleForm.duration || '60'}
                      onChange={(e) => setScheduleForm({...scheduleForm, duration: e.target.value})}
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={() => { handleCreateSchedule(); setScheduleTab('list'); }}
                    disabled={savingSchedule || !scheduleForm.exam_title || !scheduleForm.date}
                    className="gap-2"
                    style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
                  >
                    {savingSchedule ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />}
                    Planifier
                  </Button>
                </div>
              </div>
            )}

            {/* LIST TAB */}
            {scheduleTab === 'list' && (
              <div className="space-y-3">
                {schedulesLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : schedules.length === 0 ? (
                  <div className="text-center py-12">
                    <CalendarDays className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-muted-foreground text-sm">Aucun examen planifié pour le moment</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => setScheduleTab('create')}>
                      <CalendarPlus className="h-4 w-4 mr-1" />
                      Planifier un examen
                    </Button>
                  </div>
                ) : (
                  schedules.map((s) => {
                    const schedDate = new Date(s.scheduled_at);
                    const isPast = schedDate < new Date();
                    const isEditing = editingSchedule?.id === s.id;
                    return (
                      <div key={s.id} className={`rounded-xl border p-4 transition-all ${isPast ? 'opacity-50 bg-muted/20' : 'bg-white dark:bg-zinc-900 hover:shadow-md'}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            {isEditing ? (
                              <div className="space-y-2 mb-2">
                                <Input
                                  className="h-8 text-sm font-semibold"
                                  value={editingSchedule.exam_title}
                                  onChange={(e) => setEditingSchedule({...editingSchedule, exam_title: e.target.value})}
                                  placeholder="Nom de l'examen"
                                />
                                <div className="flex gap-2">
                                  <Input type="date" className="h-8 text-xs" value={editingSchedule.scheduled_at?.split('T')[0] || ''} onChange={(e) => setEditingSchedule({...editingSchedule, scheduled_at: `${e.target.value}T${editingSchedule.scheduled_at?.split('T')[1] || '09:00:00'}`})} />
                                  <Input type="time" className="h-8 text-xs" value={editingSchedule.scheduled_at?.split('T')[1]?.substring(0,5) || '09:00'} onChange={(e) => setEditingSchedule({...editingSchedule, scheduled_at: `${editingSchedule.scheduled_at?.split('T')[0] || ''}T${e.target.value}:00`})} />
                                  <Input type="number" className="h-8 text-xs w-20" min="10" step="5" placeholder="min" value={editingSchedule.duration_minutes || 60} onChange={(e) => setEditingSchedule({...editingSchedule, duration_minutes: parseInt(e.target.value) || 60})} />
                                  <span className="text-xs text-muted-foreground self-center">min</span>
                                </div>
                              </div>
                            ) : (
                              <>
                                <h4 className="font-semibold text-sm">{s.exam_title}</h4>
                                <div className="flex items-center flex-wrap gap-3 mt-1.5 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <CalendarDays className="h-3 w-3" />
                                    {schedDate.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} à {schedDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {s.duration_minutes} min
                                  </span>
                                  {s.course_name && (
                                    <Badge variant="outline" className="text-xs">{s.course_name}</Badge>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {isPast ? (
                              <Badge variant="secondary" className="text-xs">Passé</Badge>
                            ) : (
                              <>
                                <Badge className="text-xs bg-emerald-500 text-white">À venir</Badge>
                                {isEditing ? (
                                  <div className="flex gap-1 ml-1">
                                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingSchedule(null)}>Annuler</Button>
                                    <Button size="sm" className="h-7 text-xs" onClick={handleUpdateSchedule} style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>Sauver</Button>
                                  </div>
                                ) : (
                                  <div className="flex gap-0.5 ml-1">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-xs gap-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                      onClick={() => handleSendNotification(s)}
                                      disabled={sendingNotifId === s.id}
                                    >
                                      {sendingNotifId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                                      Notifier
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingSchedule({...s})}>
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteSchedule(s.id)}>
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

    </WorkspaceLayout>
  );
}
